import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import GlassCard from '../components/shared/GlassCard';
import Icon from '../components/shared/Icon';
import Skeleton from '../components/shared/Skeleton';
import { MOCK_FEED, MOCK_ATHLETES, MOCK_COMMENTS } from '../utils/mockData';
import './FeedPage.css';

const TYPE_ICON = {
  pr:     'trophy',
  log:    'log',
  streak: 'streak',
  assign: 'assign',
  video:  'play',
};

function timeAgo(ts) {
  const diffMs = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function getAthleteName(athleteId) {
  const a = MOCK_ATHLETES.find((x) => x.id === athleteId);
  return a ? a.name : athleteId;
}

function CommentThread({ postId }) {
  const [comments, setComments] = useState(() =>
    (MOCK_COMMENTS[postId] || []).map((c) => ({ ...c }))
  );
  const [draft, setDraft] = useState('');

  function toggleCommentLike(id) {
    setComments((prev) =>
      prev.map((c) =>
        c.id === id
          ? { ...c, userLiked: !c.userLiked, likes: c.userLiked ? c.likes - 1 : c.likes + 1 }
          : c
      )
    );
  }

  function submitComment() {
    if (!draft.trim()) return;
    const newComment = {
      id: `c-${Date.now()}`,
      author: 'You',
      text: draft.trim(),
      timestamp: new Date().toISOString(),
      likes: 0,
      userLiked: false,
    };
    setComments((prev) => [...prev, newComment]);
    setDraft('');
  }

  return (
    <div className="feed-comments">
      {comments.map((c) => (
        <div key={c.id} className="feed-comment">
          <div className="feed-comment__avatar">{c.author[0]}</div>
          <div className="feed-comment__body">
            <div className="feed-comment__header">
              <span className="feed-comment__author">{c.author}</span>
              <span className="feed-comment__time">{timeAgo(c.timestamp)}</span>
            </div>
            <p className="feed-comment__text">{c.text}</p>
          </div>
          <button
            className={`feed-comment__like ${c.userLiked ? 'feed-comment__like--active' : ''}`}
            onClick={() => toggleCommentLike(c.id)}
            aria-label="Like comment"
          >
            <Icon name="thumbs-up" size={12} />
            {c.likes > 0 && <span>{c.likes}</span>}
          </button>
        </div>
      ))}
      <div className="feed-comment-input">
        <input
          className="feed-comment-input__field"
          type="text"
          placeholder="Add a comment…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submitComment()}
          maxLength={280}
        />
        <button
          className="feed-comment-input__send"
          onClick={submitComment}
          disabled={!draft.trim()}
          aria-label="Post comment"
        >
          <Icon name="forward" size={14} />
        </button>
      </div>
    </div>
  );
}

function FeedItem({ item }) {
  const [likes, setLikes] = useState(item.likes ?? 0);
  const [dislikes, setDislikes] = useState(item.dislikes ?? 0);
  const [reaction, setReaction] = useState(null); // 'like' | 'dislike' | null
  const [bookmarked, setBookmarked] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);

  function handleReact(type) {
    if (reaction === type) {
      // un-react
      setReaction(null);
      type === 'like' ? setLikes((n) => n - 1) : setDislikes((n) => n - 1);
    } else {
      // switch or first react
      if (reaction === 'like') setLikes((n) => n - 1);
      if (reaction === 'dislike') setDislikes((n) => n - 1);
      setReaction(type);
      type === 'like' ? setLikes((n) => n + 1) : setDislikes((n) => n + 1);
    }
  }

  const hasComments = !!(MOCK_COMMENTS[item.id]?.length);

  return (
    <GlassCard size="sm" className="feed-item">
      {item.type === 'video' ? (
        <div className="feed-video" style={{ background: item.gradient }}>
          <div className="feed-video__play">
            <Icon name="play" size={22} />
          </div>
          {item.duration && <span className="feed-video__duration">{item.duration}</span>}
        </div>
      ) : (
        <div className={`feed-item__icon-wrap feed-item__icon-wrap--${item.type}`}>
          <Icon name={TYPE_ICON[item.type] || 'note'} size={16} />
        </div>
      )}

      <div className="feed-item__body">
        <Link className="feed-item__author feed-item__author-link" to={`/app/athlete/${item.athleteId}`}>
          {getAthleteName(item.athleteId)}
        </Link>
        <p className="feed-item__text">{item.text}</p>
        <span className="feed-item__meta">{timeAgo(item.timestamp)}</span>
      </div>

      <div className="feed-item__reactions">
        <button
          className={`feed-reaction ${reaction === 'like' ? 'feed-reaction--active-up' : ''}`}
          onClick={() => handleReact('like')}
          aria-label="Like"
        >
          <Icon name="thumbs-up" size={14} />
          {likes > 0 && <span>{likes}</span>}
        </button>
        <button
          className={`feed-reaction ${reaction === 'dislike' ? 'feed-reaction--active-down' : ''}`}
          onClick={() => handleReact('dislike')}
          aria-label="Dislike"
        >
          <Icon name="thumbs-down" size={14} />
          {dislikes > 0 && <span>{dislikes}</span>}
        </button>
        <button
          className={`feed-reaction ${bookmarked ? 'feed-reaction--bookmarked' : ''}`}
          onClick={() => setBookmarked((b) => !b)}
          aria-label="Bookmark"
        >
          <Icon name="bookmark" size={14} />
        </button>
        <button
          className={`feed-reaction ${commentsOpen ? 'feed-reaction--comment-open' : ''}`}
          onClick={() => setCommentsOpen((o) => !o)}
          aria-label="Comments"
        >
          <Icon name="comment" size={14} />
          {hasComments && !commentsOpen && (
            <span>{MOCK_COMMENTS[item.id].length}</span>
          )}
        </button>
      </div>

      {commentsOpen && <CommentThread postId={item.id} />}
    </GlassCard>
  );
}

export default function FeedPage() {
  const [feedMode, setFeedMode] = useState('team'); // 'team' | 'public'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(t);
  }, []);

  const filtered = feedMode === 'public'
    ? MOCK_FEED.filter((i) => i.athleteId !== 'athlete-001')
    : MOCK_FEED;

  if (loading) {
    return (
      <div className="feed-page">
        <div className="feed-page__header">
          <Skeleton width={80} height={28} />
          <Skeleton width={140} height={36} rounded />
        </div>
        <div className="feed-list">
          {[1,2,3,4].map((i) => (
            <GlassCard key={i} style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 'var(--space-4)' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <Skeleton width={40} height={40} rounded />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <Skeleton width="60%" height={14} />
                  <Skeleton width="40%" height={12} />
                </div>
              </div>
              <Skeleton width="100%" height={16} />
              <Skeleton width="80%" height={16} />
            </GlassCard>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="feed-page">
      <div className="feed-page__header">
        <h1 className="feed-page__title">Feed</h1>
        <div className="feed-toggle">
          <button
            className={`feed-toggle__btn ${feedMode === 'team' ? 'feed-toggle__btn--active' : ''}`}
            onClick={() => setFeedMode('team')}
          >
            Team
          </button>
          <button
            className={`feed-toggle__btn ${feedMode === 'public' ? 'feed-toggle__btn--active' : ''}`}
            onClick={() => setFeedMode('public')}
          >
            Public
          </button>
        </div>
      </div>

      <div className="feed-list">
        {filtered.length === 0 && (
          <p className="feed-empty">No posts yet. Start training to share activity.</p>
        )}
        {filtered.map((item) => (
          <FeedItem key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

